<template>
<section :class="{'is-compare': dataList.length > 1}" class="stage-data-wrapper">
  <section class="ion-ocec-count">
    <section class="ion-ocec-chart">
      <section class="value-chart">
        <div>
          <select-ctrl-group :ids="selectedNames" :options="filterFactors" id-prop="name" />
        </div>
        <div>
          <e-chart ref="ion-ocec-value-chart" @initialized="onInitialized1" @destroy="onDestroy1" />
        </div>
      </section>
      <section class="percentage-chart">
        <div>
          <select-ctrl-group :ids="selectedNames" :options="filterFactors" id-prop="name" />
        </div>
        <div>
          <e-chart ref="ion-ocec-percentage-chart" @initialized="onInitialized2" @destroy="onDestroy2" />
        </div>
      </section>
    </section>

    <section
      v-for="(totalTime, index) in totalTimes"
      :key="index"
      class="data-time-info-table-wrapper"
    >
      <section class="data-time-info-table-header">{{totalTime.suffix}}离子和OCEC因子有效小时数(总数：{{totalTime.duration}}小时)</section>
      <section class="data-time-info-table-body">
        <data-table
          v-if="ionOcecTableDataList[index]"
          ref="ionOcecDataTable"
          :key="tableKey"
          :columns="ionOcecTableColumns"
          :data="ionOcecTableDataList[index]"
        />
      </section>
    </section>
  </section>
</section>
</template>

<script>
import BaseStore from '../../../../components/BaseStore'
import {FACTOR_OPTS} from './constants'
import DataTable from '../../../../components/Data/DataTable'
import SelectCtrlGroup from '../../../../components/SelectCtrlGroup/index'

export default {
  name: 'StageAnalysis',
  components: {SelectCtrlGroup, DataTable},
  extends: BaseStore,
  props: {
    query: {
      required: true,
      type: Object
    },
    compareQuery: {
      required: true,
      type: Object
    }
  },
  data () {
    return {
      dataList: Object.freeze([]),
      tableKey: new Date().getTime(),
      totalTimes: Object.freeze([]),
      selectedNames: []
    }
  },
  computed: {
    siteMap () {
      return this.profileSites.reduce((map, site) => {
        map[site.id] = site
        return map
      }, {})
    },
    filterFactors: () => FACTOR_OPTS.filter(factorOpt => factorOpt.compare),
    displaySites () {
      let {siteMap} = this
      return this.query.siteIds.map(siteId => siteMap[siteId].name)
    },
    ionOcecTableColumns () {
      return this.filterFactors.reduce((list, factor) => {
        list.push({
          id: factor.id,
          name: factor.name,
          width: 120,
          align: 'left'
        })
        return list
      }, [{
        id: 'siteName',
        name: '站房名称',
        width: 200,
        align: 'left',
        fixed: true
      }])
    },
    ionOcecTableDataList () {
      let {filterFactors, totalTimes} = this
      return this.dataList.map((dataList, index) => {
        let {duration} = totalTimes[index]
        return dataList.map(data => filterFactors.reduce((dataMap, {id}) => {
          let count = data[`${id}Count`] || 0
          dataMap[id] = `${count} (${Math.round(count / duration * 100)}%)`
          return dataMap
        }, {
          siteName: data.siteName
        }))
      })
    }
  },
  methods: {
    getData () {
      let {query, compareQuery, displaySites} = this
      let {dataType, isAuditCompleted, siteIds, factorIds, isMultipleIntegral} = query
      let granularity = 'arbitrary'

      this.changeLoadStatus(true)

      let querys = [this.$api.Data.getHistoryData({
        beginTime: query.beginTime,
        endTime: query.endTime,
        dataType,
        isAuditCompleted,
        isMultipleIntegral: isMultipleIntegral,
        siteIds,
        factorIds,
        granularity
      })]

      let {beginTime, endTime} = compareQuery
      let xAxis = [{
        type: 'category',
        data: displaySites,
        position: 'bottom'
      }]

      if (beginTime && endTime) {
        querys.push(this.$api.Data.getHistoryData({
          beginTime: compareQuery.beginTime,
          endTime: compareQuery.endTime,
          isMultipleIntegral: isMultipleIntegral,
          dataType,
          isAuditCompleted,
          siteIds,
          factorIds,
          granularity
        }))

        xAxis[0].offset = 30

        xAxis.unshift({
          type: 'category',
          data: displaySites.reduce((list, siteName) => {
            list.push('', '当前', '对比', '')
            return list
          }, []),
          axisLabel: {
            rotate: 45
          },
          axisTick: {
            show: false
          }
        })
      }

      this.xAxis = xAxis
      Promise.all(querys).then(this.onDataReceived).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    onDataReceived (data) {
      let {siteMap, filterFactors, factorMap} = this
      let {siteIds} = this.query
      let dataList = []
      this.setTotalTimes()
      this.setFormatDataList(dataList, data[0], siteIds, siteMap, filterFactors, factorMap)
      this.setFormatDataList(dataList, data[1], siteIds, siteMap, filterFactors, factorMap)
      this.dataList = Object.freeze(dataList)
      this.tableKey = new Date().getTime()

      this.$nextTick(() => {
        this.renderChart()
        this.doTableLayout('ionOcecDataTable')
      })
    },
    setTotalTimes () {
      let {query, compareQuery} = this
      let beginTS = new Date(query.beginTime).getTime()
      let endTS = new Date(query.endTime).getTime()
      let duration = Math.ceil((endTS - beginTS) / 3600000)
      let totalTimes = [{
        suffix: '当前',
        duration
      }]

      let {beginTime, endTime} = compareQuery

      if (beginTime && endTime) {
        let beginTS = new Date(beginTime).getTime()
        let endTS = new Date(endTime).getTime()
        let duration = Math.ceil((endTS - beginTS) / 3600000)

        totalTimes.push({
          suffix: '对比',
          duration
        })
      }

      this.totalTimes = Object.freeze(totalTimes)
    },
    setFormatDataList (formatDataList, dataList, siteIds, siteMap, filterFactors, factorMap) {
      if (!Array.isArray(dataList)) {
        return
      }

      let dataMap = siteIds.reduce((map, siteId) => {
        map[siteId] = {
          siteName: siteMap[siteId].name
        }
        return map
      }, {})

      dataList.forEach(data => {
        let siteData = dataMap[data.siteId]
        let {factorId} = data
        siteData[factorId] = data.value
        siteData[`${factorId}Count`] = data.count
      })

      formatDataList.push(Object.values(dataMap).map(data => filterFactors.reduce((newData, factor) => {
        let factorId = factor.id
        newData[factorId] = factor.getVal(data, factorMap)
        return newData
      }, {
        ...data
      })))
    },
    renderChart () {
      let {dataList, xAxis} = this
      let series = this.filterFactors.map(({id, name, chart}) => ({
        name,
        type: chart,
        stack: 'ion-ocec',
        barMaxWidth: 30,
        data: dataList.length > 1 ? this.displaySites.reduce((list, site, index) => {
          list.push(null)
          dataList.forEach(dataList => {
            list.push(dataList[index][id])
          })
          list.push(null)
          return list
        }, []) : dataList[0].map(data => data[id])
      }))
      this.renderValueChart(series, xAxis, 'ion-ocec-value-chart')
      this.renderPercentageChart(series, xAxis, 'ion-ocec-percentage-chart')
    },
    renderValueChart (series, xAxis, ref) {
      let chartOpt = {
        title: {
          text: '离子和OCEC 污染阶段分析(堆叠)',
          left: 'center'
        },
        toolbox: {
          feature: {
            saveAsImage: {}
          },
          top: -6,
          right: 23
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'none'
          },
          appendToBody: true,
          formatter: params => params[0].axisValue ? params.reduce((list, {marker, seriesName, value}) => {
            list.push(`<div style="font-size:12px;">${marker}<span>${seriesName}</span><span style="padding-left:20px;float:right;">${Number.isFinite(value) ? value : '--'}</span></div>`)
            return list
          }, [`<div style="font-size:14px;color:#666;font-weight:400;line-height:1;margin-bottom:5px;">${params[0].axisValue}</div>`]).join('') : ''
        },
        legend: {
          top: 30,
          selected: this.getSelected()
        },
        grid: {
          top: 110,
          right: 0,
          bottom: 0,
          left: 10,
          containLabel: true
        },
        xAxis,
        yAxis: [{
          type: 'value',
          name: 'μg/m3',
          axisLine: {
            show: false
          },
          splitLine: {
            show: true
          }
        }, {
          type: 'value'
        }],
        series
      }

      let chart = this.$refs[ref]
      chart.resize()
      chart.setOpt(chartOpt)
    },
    renderPercentageChart (series, xAxis, ref) {
      let dataValues = series[0].data
      let totals = dataValues.map((value, index) => series.reduce((sum, {data}) => sum + data[index], 0))
      let chartOpt = {
        title: {
          text: '离子和OCEC 污染阶段分析(百分比)',
          left: 'center'
        },
        toolbox: {
          feature: {
            saveAsImage: {}
          },
          top: -6,
          right: 23
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'none'
          },
          appendToBody: true,
          formatter: params => params[0].axisValue ? params.reduce((list, {marker, seriesName, value}) => {
            list.push(`<div style="font-size:12px;">${marker}<span>${seriesName}</span><span style="padding-left:20px;float:right;">${Number.isFinite(value) ? Number(value.toFixed(2)) : '--'}</span></div>`)
            return list
          }, [`<div style="font-size:14px;color:#666;font-weight:400;line-height:1;margin-bottom:5px;">${params[0].axisValue}</div>`]).join('') : ''
        },
        legend: {
          top: 30,
          selected: this.getSelected()
        },
        grid: {
          top: 110,
          right: 0,
          bottom: 0,
          left: 10,
          containLabel: true
        },
        xAxis,
        yAxis: [{
          type: 'value',
          name: '%',
          axisLine: {
            show: false
          },
          splitLine: {
            show: true
          }
        }, {
          type: 'value'
        }],
        series: series.map(series => ({
          name: series.name,
          type: series.type,
          stack: series.stack,
          barMaxWidth: series.barMaxWidth,
          data: series.data.map((value, index) => Number.isFinite(value) ? (value / totals[index] * 100) : null)
        }))
      }

      let chart = this.$refs[ref]
      chart.resize()
      chart.setOpt(chartOpt)
    },
    doTableLayout (ref) {
      let dataTables = this.$refs[ref]

      if (Array.isArray(dataTables)) {
        dataTables.forEach(dataTable => dataTable.doLayout())
      }
    },
    getSelected () {
      let factorIdMap = this.selectedNames.reduce((map, name) => {
        map[name] = true
        return map
      }, {})

      this.filterFactors.reduce((map, {name}) => {
        if (!map[name]) {
          map[name] = false
        }

        return map
      }, factorIdMap)

      return factorIdMap
    },
    onLegendSelectChanged1 (param) {
      let selected = Object.freeze(JSON.parse(JSON.stringify(param.selected)))
      this.selectedNames = Object.entries(selected).filter(selected => selected[1]).map(selected => selected[0])
      this.$refs['ion-ocec-percentage-chart'].setOpt({
        legend: {
          selected
        }
      }, true)
    },
    onLegendSelectChanged2 (param) {
      let selected = Object.freeze(JSON.parse(JSON.stringify(param.selected)))
      this.selectedNames = Object.entries(selected).filter(selected => selected[1]).map(selected => selected[0])
      this.$refs['ion-ocec-value-chart'].setOpt({
        legend: {
          selected
        }
      }, true)
    },
    onInitialized1 (chartObj) {
      this.initialized = true
      chartObj.on('legendselectchanged', this.onLegendSelectChanged1)
    },
    onDestroy1 (chartObj) {
      chartObj.off('legendselectchanged', this.onLegendSelectChanged1)
      this.initialized = false
    },
    onInitialized2 (chartObj) {
      this.initialized = true
      chartObj.on('legendselectchanged', this.onLegendSelectChanged2)
    },
    onDestroy2 (chartObj) {
      chartObj.off('legendselectchanged', this.onLegendSelectChanged2)
      this.initialized = false
    }
  },
  watch: {
    filterFactors: {
      immediate: true,
      handler (filterFactors) {
        if (filterFactors.length === 0) {
          return
        }

        this.selectedNames = filterFactors.map(factor => factor.name)
      }
    },
    selectedNames (selectedNames, old) {
      if (selectedNames !== old) {
        return
      }

      let chartObj = this.$refs['ion-ocec-value-chart']
      let chartOpt = chartObj.getOpt()

      if (!chartOpt) {
        return
      }

      let selected = this.getSelected()

      chartObj.setOpt({
        legend: {
          selected
        }
      }, true)

      this.$refs['ion-ocec-percentage-chart'].setOpt({
        legend: {
          selected
        }
      }, true)
    }
  }
}
</script>

<style lang="scss" scoped>
.stage-data-wrapper {
  $CHART-WIDTH: 465px;

  width: 100%;
  height: 100%;
  overflow: auto;

  &.is-compare {
    > * {
      height: 100%;
      padding-left: $CHART-WIDTH;
    }

    .ion-ocec-chart {
      position: absolute;
      width: $CHART-WIDTH;
      height: 100%;
      left: 0;
      margin-bottom: 0;
    }

    .value-chart,
    .percentage-chart {
      width: 100%;
      height: 48%;
    }

    .percentage-chart {
      top: 4%;
    }

    .data-time-info-table-wrapper {
      height: 48%;
      margin-top: 0;

      & + .data-time-info-table-wrapper {
        margin-top: 4%;
      }
    }
  }

  > * {
    position: relative;
  }

  .ion-ocec-chart {
    width: 100%;
    height: 402px;
    margin-bottom: 12px;
  }

  .value-chart,
  .percentage-chart {
    position: relative;
    display: grid;
    grid-template-rows: max-content auto;
    float: left;
    width: $CHART-WIDTH;
    height: 100%;
    margin-right: 1rem;

    > * {
      min: {
        width: 0;
        height: 0;
      };

      &:nth-child(1) {
        display: flex;
        justify-content: center;
      }
    }
  }

  .data-time-info-table-wrapper {
    position: relative;
    width: 100%;
    height: 210px;
    padding: 40px 20px 0;

    > .data-time-info-table-header {
      position: absolute;
      font: {
        size: 18px;
        weight: bold;
      }
      top: 0;
      left: 0;
      width: 100%;
      height: 25px;
      text-align: center;
    }

    > .data-time-info-table-body {
      width: 100%;
      height: 100%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/03Pollution/02Pollutant/StageAnalysis.vue